import React from 'react';
import { Box, Paper, Typography, Button, TextField, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import LoadingSpinner from '../../components/global/LoadingSpinner';

type ListWebsiteIdsRequest = {
  filterExampleSites?: boolean;
};

const SuperAdminDashboard: React.FC = () => {
  const [siteId, setSiteId] = React.useState('');
  const navigate = useNavigate();

  const useListWebsiteIds = useQuery({
    queryKey: ['websiteIds'],
    queryFn: async () => {
      const listWebsiteIds = httpsCallable<ListWebsiteIdsRequest, string[]>(functions, 'listWebsiteIds');
      const result = await listWebsiteIds({ filterExampleSites: false });
      return result.data;
    }
  });

  const handleManageClick = () => {
    if (siteId.trim()) {
      navigate(`/admin/superadmin/manage/${siteId.trim()}`);
    }
  };

  const handleQuickAccessClick = (id: string) => {
    navigate(`/admin/superadmin/manage/${id}`);
  };

  const handleEditSiteClick = (id: string) => {
    window.location.assign(`/admin/superadmin/site?tenant=${encodeURIComponent(id)}`);
  };

  const handleAnalyticsClick = () => {
    navigate('/admin/superadmin/analytics', { state: { websiteIds } });
  };

  const isLoading = useListWebsiteIds.isLoading;
  const isError = useListWebsiteIds.isError;
  const websiteIds = useListWebsiteIds.data ?? [];

  if (isLoading) return <LoadingSpinner label="Loading businesses" />;
  if (isError) return <p>Error loading sites.</p>;

  return (
    <Box p={4}>
      <Typography variant="h3" gutterBottom>
        SuperAdmin Dashboard
      </Typography>

      <Button variant="contained" onClick={() => navigate('/admin/superadmin/provision')}>
        Provision New Website
      </Button>

      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Analytics
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAnalyticsClick}>
          View Cross-Business Analytics
        </Button>
      </Paper>

      {/* Manual entry */}
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Manage a Site
        </Typography>
        <TextField
          fullWidth
          label="Enter Site ID"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="primary" onClick={handleManageClick} disabled={!siteId.trim()}>
            Manage Site
          </Button>
          <Button variant="outlined" onClick={() => handleEditSiteClick(siteId.trim())} disabled={!siteId.trim()}>
            Edit Website
          </Button>
        </Stack>
      </Paper>

      {/* Quick-access list */}
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Links
        </Typography>
        <Stack spacing={2} mt={2}>
          {websiteIds.map((id) => (
            <Stack key={id} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" onClick={() => handleQuickAccessClick(id)} sx={{ textTransform: 'none', flex: 1 }}>
                Manage {id}
              </Button>
              <Button variant="contained" onClick={() => handleEditSiteClick(id)} sx={{ textTransform: 'none' }}>
                Edit Site
              </Button>
            </Stack>
          ))}
        </Stack>
      </Paper>

    </Box>
  );
};

export default SuperAdminDashboard;
